import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useKeyboardShortcut } from 'src/hooks';
import {
  LearningSessionAction,
  ResponseState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { AudioResponseButton, ButtonState } from './AudioResponseButton';
import * as S from './styles';

export const getButtonState = (
  /** The value for this button */
  choice: string,
  /** The value the user has selected */
  userAnswer: string,
  responseState: Exclude<ResponseState, 'nearlyCorrect'>,
  correctChoice: string,
  showCorrectAnswer?: boolean,
): ButtonState => {
  /* istanbul ignore next : dev tools only */
  if (showCorrectAnswer && choice === correctChoice) {
    return 'green';
  }

  if (responseState === 'Unanswered') return 'default';

  if (responseState === 'Selected') {
    if (choice === userAnswer) return 'yellow';
  }

  if (responseState === 'Correct') {
    if (choice === userAnswer) return 'green';
  }

  if (responseState === 'Incorrect') {
    if (choice === userAnswer) return 'red';
    if (choice === correctChoice) return 'green';
  }

  return 'default';
};

type AudioMCResponseProps = {
  choices: string[];
  correctChoice: string;
  responseState: ResponseState;
  userAnswer: string;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  showCorrectAnswer: boolean;
};

type Direction = 'left' | 'right';

export const getNextFocusedButtonIndex = (
  numItems: number,
  direction: Direction,
  currentlyFocusedIndex: number | null,
): number => {
  if (currentlyFocusedIndex === null) {
    // right
    if (direction === 'right') return 0;

    // left
    return numItems - 1;
  } else {
    // right
    if (direction === 'right') return (currentlyFocusedIndex + 1) % numItems;

    // left
    const next = currentlyFocusedIndex - 1;
    if (next < 0) return numItems - 1;
    return next;
  }
};

export const AudioMCResponse: FC<AudioMCResponseProps> = ({
  choices,
  correctChoice,
  responseState,
  userAnswer,
  learningSessionDispatch,
  showCorrectAnswer,
}) => {
  if (responseState === 'NearlyCorrect') {
    throw new Error('responseState cannot be "NearlyCorrect" for MCAudioResponse');
  }

  const handleClickForChoice = (choice: string) => {
    if (userAnswer === choice) {
      // If the answer is selected, confirm it
      learningSessionDispatch({ type: 'ANSWER_TEST', answer: choice });
    } else {
      // Otherwise, select it
      learningSessionDispatch({
        type: 'TENTATIVELY_SELECT_ANSWER',
        answer: choice,
      });
    }
  };

  const [focusRefIndex, setFocusRefIndex] = useState<number | null>(null);
  const answerOneRef = useRef<HTMLDivElement>(null);
  const answerTwoRef = useRef<HTMLDivElement>(null);
  const answerThreeRef = useRef<HTMLDivElement>(null);
  const answerFourRef = useRef<HTMLDivElement>(null);
  const refs = useMemo(() => [answerOneRef, answerTwoRef, answerThreeRef, answerFourRef], []);
  const focusNext = useCallback(
    (direction: Direction) => {
      const nextFocusRefIndex = getNextFocusedButtonIndex(choices.length, direction, focusRefIndex);

      setFocusRefIndex(nextFocusRefIndex);
      const elToFocus = refs[nextFocusRefIndex];

      elToFocus.current && elToFocus.current.focus();
    },
    [focusRefIndex, refs, choices.length],
  );
  useKeyboardShortcut(['left', 'h'], () => focusNext('left'));
  useKeyboardShortcut(['right', 'l'], () => focusNext('right'));

  return (
    <S.Container>
      <div>
        {choices.map((choice, i) => (
          <S.ButtonPositioner key={choice}>
            <AudioResponseButton
              src={choice}
              ref={refs[i]}
              shortcutNumber={i + 1}
              handleClick={() => handleClickForChoice(choice)}
              disabled={responseState === 'Correct' || responseState === 'Incorrect'}
              buttonState={getButtonState(
                choice,
                userAnswer,
                responseState,
                correctChoice,
                showCorrectAnswer,
              )}
            />
          </S.ButtonPositioner>
        ))}
      </div>
    </S.Container>
  );
};
